import {
  MapPin,
  Phone,
  Clock,
  Facebook,
  Twitter,
  Instagram,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import Breadcrumb from '../components/Breadcrumb';
import Seo from '../components/Seo';
import { OrganizationSchema, LocalBusinessSchema } from '../components/SchemaMarkup';
import { Toaster } from 'react-hot-toast';

function Contact() {
  const breadcrumbs = [
    { label: 'Contact Us', path: '/contact' },
  ];

  return (
    <div className="font-sans text-black">
      <Seo
        title="Contact Moreflex Aviation | 24/7 VIP Airport Support"
        description="Get in touch with Moreflex Aviation for VIP meet & greet, lounge, chauffeur, and baggage services. Call, email, or message our 24/7 support team based in Mombasa, Kenya."
        path="/contact"
      />
      <OrganizationSchema />
      <LocalBusinessSchema />
      <Header />
      <Breadcrumb items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white py-16 overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="eyebrow justify-center mb-4 animate-fadeInTop">Get In Touch</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeInTop">
              Contact Us
            </h1>
            <p className="text-lg text-white/75 mb-8 animate-fadeIn">
              We're here to answer any questions you may have about our
              services. Reach out to us and we'll respond as soon as we can.
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#73BDF2] rounded-full opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#73BDF2] rounded-full opacity-10 transform translate-x-1/4 translate-y-1/4"></div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Contact Info */}
            <div className="animate-fadeInLeft">
              <div className="mb-12">
                <p className="eyebrow mb-3">Contact Info</p>
                <h2 className="section-title mb-6">
                  Have Questions? Contact Our 24/7 Support Team
                </h2>
                <p className="text-gray-600 mb-8">
                  Moreflex Aviation provides expert 24/7 flight support services
                  at highly competitive rates. Our dedicated team is ready to
                  assist you with all your aviation needs.
                </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                    <MapPin className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Location</h3>
                    <p className="text-gray-600">80100 Mombasa, Kenya</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                    <Phone className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Quick Contact</h3>
                    <p className="text-gray-600">Phone: +254 (102) 188-352, +254 (114) 660-056</p>
                    <p className="text-gray-600">
                      Email: info@moreflexaviation.com / customer@moreflexaviation.com /
                      meetandgreet@moreflexaviation.com / accounts@moreflexaviation.com
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4">
                    <Clock className="text-[#73BDF2]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Opening Hours</h3>
                    <p className="text-gray-600">24/7 Always Open and Operating</p>
                  </div>
                </div>

                <div className="pt-4">
                  <h3 className="font-bold text-lg mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a
                      href="#"
                      className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300"
                    >
                      <Facebook
                        size={18}
                        className="text-[#73BDF2] hover:text-white"
                      />
                    </a>
                    <a
                      href="#"
                      className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300"
                    >
                      <Twitter
                        size={18}
                        className="text-[#73BDF2] hover:text-white"
                      />
                    </a>
                    <a
                      href="#"
                      className="bg-[#73BDF2]/20 w-10 h-10 rounded-full flex items-center justify-center hover:bg-[#73BDF2] transition-colors duration-300"
                    >
                      <Instagram
                        size={18}
                        className="text-[#73BDF2] hover:text-white"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-gray-50 p-8 rounded-2xl shadow-elevated border border-gray-100 animate-fadeInRight">
              <p className="eyebrow mb-3">Send a Message</p>
              <h2 className="section-title mb-6">Send Your Message</h2>
              <p className="text-gray-600 mb-8">
                Our 24 hour support team is always ready to listen to you. We
                work round the clock, seven days a week just to make sure you
                get the best service whenever you need it.
              </p>

              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="eyebrow justify-center mb-3">Find Us</p>
            <h2 className="section-title mb-4">Our Location</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Visit us at our headquarters or at any of our global offices. Our
              team is ready to assist you in person.
            </p>
          </div>

          <div className="bg-white p-4 rounded-2xl shadow-elevated border border-gray-100">
            <div className="aspect-w-16 aspect-h-9 h-[400px] bg-gray-200 rounded-lg overflow-hidden">
              {/* Google Maps iframe */}
              <div className="mapouter relative w-full h-[400px] text-right">
                <div className="gmap_canvas w-full h-full overflow-hidden bg-transparent">
                  <iframe
                    className="gmap_iframe w-full h-full border-0"
                    scrolling="no"
                    src="https://maps.google.com/maps?width=600&amp;height=400&amp;hl=en&amp;q=Mombasa,Changamwe chaani&amp;t=&amp;z=12&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                  ></iframe>
                </div>
                <a href="https://sprunkin.com/" className="text-blue-500 hover:underline">
                  Sprunki Phases
                </a>

                <style>
                  {`
                    .mapouter {
                      position: relative;
                      width: 100%;
                      height: 400px;
                    }
                    .gmap_canvas {
                      width: 100%;
                      height: 400px;
                    }
                    .gmap_iframe {
                      height: 400px !important;
                    }
                  `}
                </style>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster position="top-right" />
    </div>
  );
}

export default Contact;
